import { AddressForm } from '../../components/address-form';
import {
  Button,
  ButtonVariants,
  Typography,
} from '../../components/primitives';
import Alert, { AlertProps } from '../../components/ui/Alert';
import { RequiredFieldIndicator } from '../../components/ui/RequiredFieldIndicator';
import {
  useAddressAlertProps,
  useAddressForm,
} from '../../hooks/address-form.hook';
import { testId } from '../../utils';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

export const EditAddress: React.FC = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.user' });
  const navigate = useNavigate();
  const params = useParams();

  const {
    form,
    regionQuery,
    countries,
    countriesLoading,
    AddressSchema,
    onCancel,
  } = useAddressForm();

  type AddressSchema = z.infer<typeof AddressSchema>;

  const updateAddressMutation = Cornerstone.useUpdateAddress({
    mutation: {
      onSuccess: () => {
        navigate('..', {
          relative: 'route',
        });
      },
      onError: (error: any) => {
        console.error({
          title: 'Failed to update address',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const addressId = useMemo(() => Number(params.id), [params.id]);

  const { data, isLoading, error } = Cornerstone.useGetAddress(addressId);

  useEffect(() => {
    if (data) {
      form.reset(data?.data);
    }
  }, [data, form]);

  const { regions, loading: regionsLoading, label: regionLabel } = regionQuery;

  const onSubmit = React.useCallback(
    (data: AddressSchema) => {
      updateAddressMutation.mutate({ addressId, data });
    },
    [updateAddressMutation, addressId],
  );

  const alertProps = useAlertProps(updateAddressMutation, error);

  return (
    <div className="flex flex-col gap-7">
      <div className="flex justify-between items-baseline">
        <Typography variant="title-small" className="text-gray-soft">
          {t('addresses.edit_address')}
        </Typography>
        <RequiredFieldIndicator />
      </div>
      {alertProps && <Alert {...alertProps} />}
      <AddressForm
        loading={
          updateAddressMutation.isLoading ||
          countriesLoading ||
          regionsLoading ||
          isLoading
        }
        countries={countries}
        regions={regions}
        regionLabel={regionLabel}
        form={form}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="flex gap-2">
          <Button
            {...testId('save')}
            size="sm"
            type="submit"
            onClick={form.handleSubmit(onSubmit)}
            loading={updateAddressMutation.isLoading || isLoading}
            disabled={updateAddressMutation.isLoading || isLoading}
          >
            {t('settings.account.save_button')}
          </Button>

          <Button
            {...testId('cancel')}
            variant={ButtonVariants.Secondary}
            size="sm"
            onClick={onCancel}
            disabled={updateAddressMutation.isLoading || isLoading}
          >
            {t('settings.account.cancel_button')}
          </Button>
        </div>
      </AddressForm>
    </div>
  );
};
function useAlertProps(
  mutation: ReturnType<typeof Cornerstone.useUpdateAddress>,
  error: unknown,
): AlertProps | null {
  const { t } = useTranslation(undefined, { keyPrefix: 'route' });
  const addressAlertProps = useAddressAlertProps(mutation);

  if (error) {
    return {
      ...testId('alert'),
      variant: 'error',
      children: t('user.addresses.getAddress_error'),
    };
  }
  if (!mutation.error) {
    return null;
  }

  return addressAlertProps;
}
